<template>
  <div class="redeem-logs-container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>核销记录</h2>
        </div>
      </template>
      
      <!-- 搜索和筛选 -->
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="类型">
          <el-select v-model="searchForm.type" placeholder="请选择类型" clearable style="width: 150px;">
            <el-option label="全部" value="all" />
            <el-option label="卡券核销" value="coupon" />
            <el-option label="订单核销" value="order" />
          </el-select>
        </el-form-item>
        <el-form-item label="券码/订单号">
          <el-input v-model="searchForm.keyword" placeholder="请输入券码或订单号" clearable style="width: 200px;"></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="searchLogs">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
      
      <!-- 核销记录列表 -->
      <el-table :data="redeemLogs" v-loading="loading" style="width: 100%">
        <el-table-column prop="coupon_code" label="券码/订单号" width="150" />
        <el-table-column label="卡券信息" width="250">
          <template #default="scope">
            <div v-if="scope.row.coupon" class="coupon-info">
              <div class="coupon-title">{{ scope.row.coupon.title }}</div>
              <div class="coupon-subtitle">{{ scope.row.coupon.subtitle }}</div>
              <div class="coupon-meta">
                <el-tag size="small">{{ getCategoryName(scope.row.coupon.category) }}</el-tag>
                <el-tag size="small" :type="scope.row.coupon.type === 'virtual' ? 'success' : 'info'" class="type-badge">
                  {{ scope.row.coupon.type === 'virtual' ? '虚拟' : '实物' }}
                </el-tag>
              </div>
            </div>
            <div v-else class="coupon-info">
              <span class="text-muted">订单核销</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="商品信息" width="250">
          <template #default="scope">
            <div v-if="scope.row.product" class="product-info">
              <div class="product-name">{{ scope.row.product.name }}</div>
              <div class="product-price">¥{{ scope.row.product.price }}</div>
            </div>
            <div v-else-if="scope.row.items && scope.row.items.length > 0" class="product-info">
              <div v-for="(item, index) in scope.row.items" :key="index" class="order-item">
                <div class="product-name">{{ item.product_name }}</div>
                <div class="product-quantity">x{{ item.quantity }}</div>
              </div>
            </div>
            <div v-else class="product-info">
              <span class="text-muted">无商品信息</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="用户信息" width="150">
          <template #default="scope">
            <div v-if="scope.row.user" class="user-info">
              <div class="user-name">{{ scope.row.user.name }}</div>
              <div class="user-phone">{{ scope.row.user.phone }}</div>
            </div>
            <div v-else class="user-info">
              <span class="text-muted">未知用户</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="店务账号" width="120">
          <template #default="scope">
            <div v-if="scope.row.shop_account" class="shop-account-info">
              {{ scope.row.shop_account.name }}
            </div>
            <div v-else class="shop-account-info">
              <span class="text-muted">未知店务</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作人" width="120">
          <template #default="scope">
            <div v-if="scope.row.operator" class="operator-info">
              {{ scope.row.operator.name }}
            </div>
            <div v-else class="operator-info">
              <span class="text-muted">未知操作人</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="核销时间" width="180">
          <template #default="scope">
            {{ formatDateTime(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" width="200" show-overflow-tooltip />
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleViewDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 详情抽屉 -->
    <el-drawer
      v-model="detailDrawerVisible"
      title="核销记录详情"
      size="60%"
      direction="rtl"
      @close="handleDetailDrawerClose"
    >
      <div v-if="currentLog" class="detail-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="券码/订单号">{{ currentLog.coupon_code }}</el-descriptions-item>
          <el-descriptions-item label="核销时间">{{ formatDateTime(currentLog.created_at) }}</el-descriptions-item>
          
          <el-descriptions-item label="用户姓名" v-if="currentLog.user">{{ currentLog.user.name }}</el-descriptions-item>
          <el-descriptions-item label="用户电话" v-if="currentLog.user">{{ currentLog.user.phone }}</el-descriptions-item>
          
          <el-descriptions-item label="店务账号" v-if="currentLog.shop_account">{{ currentLog.shop_account.name }}</el-descriptions-item>
          <el-descriptions-item label="操作人" v-if="currentLog.operator">{{ currentLog.operator.name }}</el-descriptions-item>
          
          <el-descriptions-item label="备注" :span="2">{{ currentLog.remark || '-' }}</el-descriptions-item>
        </el-descriptions>
        
        <div v-if="currentLog.coupon" class="section">
          <h3>卡券信息</h3>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="卡券标题">{{ currentLog.coupon.title }}</el-descriptions-item>
            <el-descriptions-item label="卡券副标题">{{ currentLog.coupon.subtitle }}</el-descriptions-item>
            <el-descriptions-item label="卡券类型">
              <el-tag :type="currentLog.coupon.type === 'virtual' ? 'success' : 'info'">
                {{ currentLog.coupon.type === 'virtual' ? '虚拟券' : '实物券' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="卡券分类">
              <el-tag>{{ getCategoryName(currentLog.coupon.category) }}</el-tag>
            </el-descriptions-item>
          </el-descriptions>
        </div>
        
        <div v-if="currentLog.product" class="section">
          <h3>商品信息</h3>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="商品名称">{{ currentLog.product.name }}</el-descriptions-item>
            <el-descriptions-item label="商品价格">¥{{ currentLog.product.price }}</el-descriptions-item>
            <el-descriptions-item label="商品分类">{{ currentLog.product.category }}</el-descriptions-item>
          </el-descriptions>
        </div>
        
        <div v-if="currentLog.items && currentLog.items.length > 0" class="section">
          <h3>订单商品</h3>
          <el-table :data="currentLog.items" style="width: 100%">
            <el-table-column prop="product_name" label="商品名称" />
            <el-table-column prop="quantity" label="数量" width="100" />
            <el-table-column prop="unit_price" label="单价" width="120">
              <template #default="scope">¥{{ scope.row.unit_price }}</template>
            </el-table-column>
            <el-table-column prop="subtotal" label="小计" width="120">
              <template #default="scope">¥{{ scope.row.subtotal }}</template>
            </el-table-column>
          </el-table>
        </div>
        
        <div v-if="currentLog.amount !== undefined" class="section">
          <h3>订单金额</h3>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="订单总额">¥{{ currentLog.amount }}</el-descriptions-item>
            <el-descriptions-item label="支付方式">{{ currentLog.payment_method || '-' }}</el-descriptions-item>
            <el-descriptions-item label="支付状态">{{ currentLog.payment_status || '-' }}</el-descriptions-item>
            <el-descriptions-item label="现金金额">¥{{ currentLog.cash_amount || 0 }}</el-descriptions-item>
            <el-descriptions-item label="使用积分">{{ currentLog.points_used || 0 }}</el-descriptions-item>
            <el-descriptions-item label="优惠金额">¥{{ currentLog.discount_amount || 0 }}</el-descriptions-item>
          </el-descriptions>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import * as couponApi from '../../api/coupon';

const loading = ref(false);
const detailDrawerVisible = ref(false);
const currentLog = ref(null);

const searchForm = ref({
  type: 'all',
  keyword: ''
});

const redeemLogs = ref([]);
const pagination = ref({
  page: 1,
  pageSize: 20,
  total: 0
});

const couponCategories = {
  'authentication': '鉴别服务券',
  'maintenance': '保养券',
  'pre_order': '新品预定权益',
  'activity_invitation': '活动邀约权益',
  'home_recycle': '上门回收券',
  'repair': '维修券',
  'peripheral_exchange': '周边兑换券',
  'gift_exchange': '赠品兑换券'
};

function getCategoryName(category) {
  return couponCategories[category] || category;
}

async function fetchRedeemLogs() {
  loading.value = true;
  try {
    const response = await couponApi.getRedeemLogs({
      page: pagination.value.page,
      page_size: pagination.value.pageSize,
      type: searchForm.value.type
    });
    
    if (response.code === 200 || response.success) {
      redeemLogs.value = response.data.data || response.data || [];
      pagination.value.total = response.data.total || 0;
    }
  } catch (error) {
    ElMessage.error('获取核销记录失败：' + (error.message || '未知错误'));
    console.error('获取核销记录失败:', error);
  } finally {
    loading.value = false;
  }
}

function searchLogs() {
  pagination.value.page = 1;
  fetchRedeemLogs();
}

function resetSearch() {
  searchForm.value = {
    type: 'all',
    keyword: ''
  };
  pagination.value.page = 1;
  fetchRedeemLogs();
}

function handleSizeChange(val) {
  pagination.value.pageSize = val;
  pagination.value.page = 1;
  fetchRedeemLogs();
}

function handleCurrentChange(val) {
  pagination.value.page = val;
  fetchRedeemLogs();
}

function handleViewDetail(log) {
  currentLog.value = log;
  detailDrawerVisible.value = true;
}

function handleDetailDrawerClose() {
  currentLog.value = null;
}

function formatDateTime(dateTime) {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

onMounted(() => {
  fetchRedeemLogs();
});
</script>

<style scoped>
.redeem-logs-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.search-form {
  margin-bottom: 20px;
}

.coupon-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.coupon-title {
  font-weight: bold;
  color: #333;
}

.coupon-subtitle {
  font-size: 12px;
  color: #666;
}

.coupon-meta {
  display: flex;
  gap: 5px;
}

.type-badge {
  margin-left: 5px;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.product-name {
  font-weight: bold;
  color: #333;
}

.product-price {
  font-size: 14px;
  color: #E6A23C;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px solid #f0f0f0;
}

.order-item:last-child {
  border-bottom: none;
}

.product-quantity {
  font-size: 12px;
  color: #999;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.user-name {
  font-weight: bold;
  color: #333;
}

.user-phone {
  font-size: 12px;
  color: #666;
}

.shop-account-info,
.operator-info {
  color: #333;
}

.text-muted {
  color: #999;
  font-size: 12px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.detail-content {
  padding: 20px;
}

.section {
  margin-top: 30px;
}

.section h3 {
  margin-bottom: 15px;
  font-size: 16px;
  color: #333;
  border-left: 4px solid #409EFF;
  padding-left: 10px;
}
</style>
